import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { initiateGoogleOAuth, initiateMicrosoftOAuth } from '../services/oauth';

const HomePage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Admin positioning mode
  const [positionMode, setPositionMode] = useState(false);
  const [positions, setPositions] = useState(() => {
    const saved = localStorage.getItem('homeButtonPositions');
    return saved ? JSON.parse(saved) : {
      memberLogin: { left: 67.7, top: 2.5, width: 8 },
      joinNow: { left: 75.8, top: 2.5, width: 8 }
    };
  });
  const [dragging, setDragging] = useState(null);

  const handleMouseDown = (buttonName, e) => {
    if (!positionMode) return;
    e.preventDefault();
    setDragging(buttonName);
  };

  const handleMouseMove = (e) => {
    if (!dragging || !positionMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setPositions(prev => ({
      ...prev,
      [dragging]: { ...prev[dragging], left: Math.max(0, Math.min(95, x)), top: Math.max(0, Math.min(95, y)) }
    }));
  };

  const handleMouseUp = () => {
    setDragging(null);
  };

  const lockPositions = () => {
    localStorage.setItem('homeButtonPositions', JSON.stringify(positions));
    alert('Positions locked! Left values:\nMember Login: ' + positions.memberLogin.left.toFixed(1) + '%\nJoin Now: ' + positions.joinNow.left.toFixed(1) + '%');
    setPositionMode(false);
  };

  // Toggle positioning mode with Ctrl+Shift+P
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        e.preventDefault();
        setPositionMode(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleGoogleSignIn = () => {
    initiateGoogleOAuth();
  };

  const handleMicrosoftSignIn = () => {
    initiateMicrosoftOAuth();
  };

  // Handle Email Sign In
  const handleEmailSignIn = () => {
    navigate('/login');
  };

  return (
    <div 
      className="relative w-full h-screen overflow-hidden bg-white"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Full Landing Page Image */}
      <img
        src="/landing-page.png"
        alt="Career Branding Network - Elevate Your Career Journey"
        className="w-full h-full object-contain"
        style={{ display: 'block' }}
      />

      {/* Positioning Mode Controls */}
      {positionMode && (
        <div className="fixed top-4 right-4 bg-black text-white p-4 rounded-lg shadow-lg z-50">
          <div className="text-sm font-bold mb-2">🎯 BUTTON POSITIONING MODE</div>
          <div className="text-xs mb-2">Drag buttons to align with icons</div>
          <div className="text-xs mb-1">Member Login: {positions.memberLogin.left.toFixed(1)}%</div>
          <div className="text-xs mb-3">Join Now: {positions.joinNow.left.toFixed(1)}%</div>
          <button 
            onClick={lockPositions}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded w-full mb-2"
          >
            🔒 LOCK POSITIONS
          </button>
          <button 
            onClick={() => setPositionMode(false)}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded w-full"
          >
            ✕ Cancel
          </button>
          <div className="text-xs mt-2 opacity-70">Press Ctrl+Shift+P to toggle</div>
        </div>
      )}

      {/* Hide the blue dot next to Learning icon - using cropped overlay */}
      <div 
        className="absolute overflow-hidden"
        style={{
          top: '2%',
          left: '41.5%',
          width: '1.2%',
          height: '2%',
        }}
      >
        <img
          src="/landing-page.png"
          alt=""
          className="absolute"
          style={{
            top: '-2%',
            left: '-4150%',
            width: '10000%',
            height: 'auto',
          }}
        />
      </div>

      {/* Clickable Overlays - Positioned Absolutely */}
      <div className="absolute inset-0">
        {/* HEADER BUTTONS - Top Navigation (approximate positions based on image) */}
        
        {/* Learning Button - Top Navigation */}
        <button
          onClick={() => navigate('/learning')}
          className="absolute opacity-0 cursor-pointer"
          style={{
            top: '2.5%',
            left: '43%',
            width: '7%',
            height: '4%',
          }}
          aria-label="Learning"
          title="Go to Learning"
        />

        {/* Jobs Button */}
        <button
          onClick={() => navigate('/jobs')}
          className="absolute opacity-0 cursor-pointer"
          style={{
            top: '2.5%',
            left: '51.5%',
            width: '5%',
            height: '4%',
          }}
          aria-label="Jobs"
          title="Go to Jobs"
        />

        {/* Profile Button */}
        <button
          onClick={() => isAuthenticated ? navigate('/profile') : navigate('/login')}
          className="absolute opacity-0 cursor-pointer"
          style={{
            top: '2.5%',
            left: '58%',
            width: '6%',
            height: '4%',
          }}
          aria-label="Profile"
          title="Go to Profile"
        />

        {/* Member Login Button - DRAGGABLE IN POSITION MODE */}
        <button
          onMouseDown={(e) => handleMouseDown('memberLogin', e)}
          onClick={(e) => {
            if (positionMode) {
              e.preventDefault();
              return;
            }
            console.log('Member login clicked');
            navigate('/login');
          }}
          className="absolute cursor-pointer"
          style={{
            top: `${positions.memberLogin.top}%`,
            left: `${positions.memberLogin.left}%`,
            width: `${positions.memberLogin.width}%`,
            height: '4%',
            opacity: positionMode ? 0.3 : 0,
            backgroundColor: positionMode ? 'blue' : 'transparent',
            border: positionMode ? '2px solid red' : 'none',
            zIndex: 10,
          }}
          aria-label="Member Login"
          title={positionMode ? `Member Login (${positions.memberLogin.left.toFixed(1)}%)` : "Member Login"}
        />

        {/* Join Now Button - DRAGGABLE IN POSITION MODE */}
        <button
          onMouseDown={(e) => handleMouseDown('joinNow', e)}
          onClick={(e) => {
            if (positionMode) {
              e.preventDefault();
              return;
            }
            console.log('Join now clicked - top');
            navigate('/register');
          }}
          className="absolute cursor-pointer"
          style={{
            top: `${positions.joinNow.top}%`,
            left: `${positions.joinNow.left}%`,
            width: `${positions.joinNow.width}%`,
            height: '4%',
            opacity: positionMode ? 0.3 : 0,
            backgroundColor: positionMode ? 'green' : 'transparent',
            border: positionMode ? '2px solid red' : 'none',
            zIndex: 10,
          }}
          aria-label="Join Now"
          title={positionMode ? `Join Now (${positions.joinNow.left.toFixed(1)}%)` : "Join Now - Register"}
        />

        {/* HERO SECTION - OAuth Buttons */}

        {/* Continue with Google Button */}
        <button
          onClick={handleGoogleSignIn}
          className="absolute opacity-0 cursor-pointer"
          style={{
            top: '43%',
            left: '12%',
            width: '24%',
            height: '5.5%',
          }}
          aria-label="Continue with Google"
          title="Continue with Google"
        />

        {/* Continue with Microsoft Button */}
        <button
          onClick={handleMicrosoftSignIn}
          className="absolute opacity-0 cursor-pointer"
          style={{
            top: '49.5%',
            left: '12%',
            width: '24%',
            height: '5.5%',
          }}
          aria-label="Continue with Microsoft"
          title="Continue with Microsoft"
        />

        {/* Sign in with Email Button */}
        <button
          onClick={handleEmailSignIn}
          className="absolute opacity-0 cursor-pointer"
          style={{
            top: '56%',
            left: '12%',
            width: '24%',
            height: '5.5%',
          }}
          aria-label="Sign in with Email"
          title="Sign in with Email"
        />

        {/* THREE PILLARS - Explore Buttons */}

        {/* Professional Networking Profile - Explore Button */}
        <button
          onClick={() => isAuthenticated ? navigate('/profile') : navigate('/register')}
          className="absolute opacity-0 cursor-pointer"
          style={{
            top: '86.5%',
            left: '23.5%',
            width: '5.5%',
            height: '2.5%',
          }}
          aria-label="Explore Professional Networking Profile"
          title="Explore Professional Networking Profile"
        />

        {/* Resume Builder & Hosting - Explore Button */}
        <button
          onClick={() => isAuthenticated ? navigate('/builder') : navigate('/register')}
          className="absolute opacity-0 cursor-pointer"
          style={{
            top: '86.5%',
            left: '48.5%',
            width: '5.5%',
            height: '2.5%',
          }}
          aria-label="Explore Resume Builder"
          title="Explore Resume Builder"
        />

        {/* Enhancing Portfolio Showcase - Explore Button */}
        <button
          onClick={() => isAuthenticated ? navigate('/portfolio') : navigate('/register')}
          className="absolute opacity-0 cursor-pointer"
          style={{
            top: '86.5%',
            left: '74.5%',
            width: '5.5%',
            height: '2.5%',
          }}
          aria-label="Explore Portfolio Showcase"
          title="Explore Portfolio Showcase"
        />

        {/* FOOTER - Join Now Link */}
        <button
          onClick={() => {
            console.log('Join now clicked - footer');
            navigate('/register');
          }}
          className="absolute cursor-pointer"
          style={{
            top: '94.5%',
            left: '57.5%',
            width: '8%',
            height: '2.5%',
            opacity: 0,
            zIndex: 10,
          }}
          aria-label="Join Now Footer"
          title="Join Now - Register"
        />
      </div>

      {/* Mobile Fallback - Show navigation on small screens */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-around items-center md:hidden z-50 shadow-lg">
        <button
          onClick={() => navigate('/learning')}
          className="flex flex-col items-center text-xs text-gray-700 hover:text-primary-600"
        >
          <span className="text-lg mb-1">📚</span>
          Learning
        </button>
        <button
          onClick={() => navigate('/jobs')}
          className="flex flex-col items-center text-xs text-gray-700 hover:text-primary-600"
        >
          <span className="text-lg mb-1">💼</span>
          Jobs
        </button>
        <button
          onClick={() => isAuthenticated ? navigate('/profile') : navigate('/login')}
          className="flex flex-col items-center text-xs text-gray-700 hover:text-primary-600"
        >
          <span className="text-lg mb-1">👤</span>
          Profile
        </button>
        <button
          onClick={() => navigate('/login')}
          className="flex flex-col items-center text-xs text-gray-700 hover:text-primary-600"
        >
          <span className="text-lg mb-1">🔐</span>
          Login
        </button>
        <button
          onClick={() => navigate('/register')}
          className="flex flex-col items-center text-xs text-white bg-primary-600 px-3 py-2 rounded-full hover:bg-primary-700"
        >
          <span className="text-sm font-semibold">Join</span>
        </button>
      </div>
    </div>
  );
};

export default HomePage;
